/**
 * The market board.
 *
 * The design problem: ten indices quoted in ten currencies. Their *levels* are
 * not comparable — the Nikkei at 65,856 and the S&P at 7,652 say nothing about
 * each other — so the only thing worth encoding visually is the **move**, which
 * is comparable everywhere. Every graphical channel here is therefore spent on
 * change, and the level is left as plain text.
 *
 * That is why this is a table with marks in it rather than a grid of coloured
 * cards. A heatmap tiled by level would rank Japan above America on the size of
 * a number that means nothing across borders. A table also *is* the table view —
 * every value is readable as text, so nothing is gated behind a hover.
 *
 * Marks used, all specified in `marks.tsx`:
 * - a sparkline over real exchange sessions (never our refresh times);
 * - a day-range meter showing where the level sits in the session;
 * - a signed change with a ▲/▼ glyph, so direction never rests on colour.
 */

import { useMemo, useState } from 'react'
import type { MarketIndex, MarketRate } from '@/types/api'
import { Badge, Panel, cx } from '@/components/ui/primitives'
import { formatDate, formatNumber, formatRelative } from '@/utils/format'
import {
  ChangePill, DayRangeMeter, Sparkline, directionOf, sourceLabel,
} from '@/components/data/marks'

/* -- Regions --------------------------------------------------------------- */

/**
 * Grouping order. Africa leads deliberately: this is a Nigerian index, and the
 * markets a reader here is closest to should not be buried under New York
 * because an alphabet or a market capitalisation says so.
 */
const REGIONS: Array<{ key: string; label: string; countries: string[] }> = [
  { key: 'africa', label: 'Africa', countries: ['ZA', 'EG', 'NG', 'KE', 'MA'] },
  { key: 'europe', label: 'Europe', countries: ['GB', 'DE', 'FR', 'EU', 'TR'] },
  { key: 'americas', label: 'Americas', countries: ['US', 'BR', 'CA'] },
  { key: 'asia', label: 'Asia-Pacific', countries: ['JP', 'HK', 'IN', 'KR', 'AU', 'SA'] },
]

function regionOf(countryCode: string): string {
  return REGIONS.find((region) => region.countries.includes(countryCode))?.key ?? 'other'
}

/**
 * The country, as a small monospaced code chip.
 *
 * Not a flag emoji, and that was a rendering decision made by looking at it:
 * Windows ships no country-flag font, so a regional-indicator pair falls back
 * to its two letters — which, printed beside the country code, rendered as
 * "EG  EGX 30  EG". One identity, shown once, that looks the same on every
 * platform.
 */
function CountryCode({ code }: { code: string }) {
  return (
    <span className="inline-flex h-4 min-w-[1.6rem] items-center justify-center rounded-sm bg-[var(--surface-sunken)] px-1 text-[0.6rem] font-medium tracking-wide text-[var(--text-tertiary)]">
      {code}
    </span>
  )
}

type SortKey = 'region' | 'move'

/* -- The board ------------------------------------------------------------- */

export function MarketBoard({ indices }: { indices: MarketIndex[] }) {
  const [sort, setSort] = useState<SortKey>('region')

  const groups = useMemo(() => {
    if (sort === 'move') {
      const ranked = [...indices].sort(
        (a, b) => Number(b.change_percent ?? 0) - Number(a.change_percent ?? 0),
      )
      return [{ key: 'move', label: 'Biggest movers first', rows: ranked }]
    }
    return REGIONS.map((region) => ({
      key: region.key,
      label: region.label,
      rows: indices.filter((index) => regionOf(index.country_code) === region.key),
    })).filter((group) => group.rows.length > 0)
  }, [indices, sort])

  if (indices.length === 0) return null

  return (
    <div>
      {/* One control row above everything it scopes — never inside a card. */}
      <div className="mb-3 flex items-center justify-end gap-1">
        <span className="mr-1 text-xs text-[var(--text-tertiary)]">Order by</span>
        {(
          [
            ['region', 'Region'],
            ['move', 'Move'],
          ] as Array<[SortKey, string]>
        ).map(([key, label]) => (
          <button
            key={key}
            type="button"
            onClick={() => setSort(key)}
            aria-pressed={sort === key}
            className={cx(
              'rounded-full border px-2.5 py-0.5 text-xs transition-colors',
              sort === key
                ? 'border-[var(--accent-border)] bg-[var(--accent-subtle)] text-[var(--accent-text)]'
                : 'border-[var(--border-subtle)] text-[var(--text-secondary)] hover:bg-[var(--surface-hover)]',
            )}
          >
            {label}
          </button>
        ))}
      </div>

      <Panel padded={false} className="overflow-x-auto">
        <table className="w-full min-w-[46rem] text-sm">
          <caption className="sr-only">
            Stock market index levels, the session&rsquo;s move, its range and a
            one-month trend.
          </caption>
          <thead>
            <tr className="border-b border-[var(--border-default)] text-left">
              <th scope="col" className="px-4 pb-2 pt-4 field-label font-semibold">
                Market
              </th>
              <th scope="col" className="px-4 pb-2 pt-4 field-label font-semibold text-right">
                Level
              </th>
              <th scope="col" className="px-4 pb-2 pt-4 field-label font-semibold text-right">
                Session
              </th>
              <th scope="col" className="hidden px-4 pb-2 pt-4 field-label font-semibold md:table-cell">
                Day range
              </th>
              <th scope="col" className="hidden px-4 pb-2 pt-4 field-label font-semibold lg:table-cell">
                One month
              </th>
              <th scope="col" className="hidden px-4 pb-2 pt-4 field-label font-semibold sm:table-cell">
                As at
              </th>
            </tr>
          </thead>

          {groups.map((group) => (
            <tbody key={group.key} className="divide-hairline">
              <tr>
                <th
                  scope="colgroup"
                  colSpan={6}
                  className="bg-[var(--surface-sunken)] px-4 py-1.5 text-left field-label font-semibold text-[var(--text-tertiary)]"
                >
                  {group.label}
                </th>
              </tr>
              {group.rows.map((index) => {
                const direction = directionOf(index.change_percent)
                return (
                  <tr key={index.symbol} className="hover:bg-[var(--surface-hover)]">
                    <td className="px-4 py-2.5">
                      <span className="flex items-center gap-2">
                        <CountryCode code={index.country_code} />
                        <span className="font-medium text-[var(--text-primary)]">
                          {index.name}
                        </span>
                        {index.stale ? (
                          <Badge tone="stale" size="xs">
                            Stale
                          </Badge>
                        ) : null}
                      </span>
                    </td>

                    <td className="px-4 py-2.5 text-right tabular-nums text-[var(--text-primary)]">
                      <span className="text-xs text-[var(--text-tertiary)]">
                        {index.currency_code}{' '}
                      </span>
                      {Number(index.value).toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </td>

                    <td className="px-4 py-2.5 text-right">
                      <ChangePill changePercent={index.change_percent} />
                    </td>

                    <td className="hidden px-4 py-2.5 md:table-cell">
                      <DayRangeMeter
                        low={index.day_low}
                        high={index.day_high}
                        value={index.value}
                        currency={index.currency_code}
                      />
                    </td>

                    <td className="hidden px-4 py-2.5 lg:table-cell">
                      <Sparkline
                        points={index.history}
                        direction={direction}
                        label={`${index.name}: ${index.history.length} sessions to ${formatDate(
                          index.observed_at,
                        )}`}
                      />
                    </td>

                    <td className="hidden px-4 py-2.5 text-xs text-[var(--text-tertiary)] sm:table-cell">
                      {formatDate(index.observed_at)}
                      {index.exchange ? (
                        <span className="block text-[0.6rem]">{index.exchange}</span>
                      ) : null}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          ))}
        </table>
      </Panel>
    </div>
  )
}

/* -- FX ------------------------------------------------------------------- */

/**
 * The naira rate a page leads with. One hero figure per view, in the same sans
 * as everything else, with proportional figures — `tabular-nums` on a display
 * number makes it look loose.
 */
export function HeroRate({ rate }: { rate: MarketRate }) {
  const direction = directionOf(rate.change_percent)
  const hasChange = rate.change_percent !== null
  const hasTrend = rate.history.length >= 2

  return (
    <div className="flex flex-wrap items-start justify-between gap-8">
      <div className="min-w-0">
        <p className="field-label text-[var(--text-tertiary)]">{rate.name}</p>
        <p className="mt-1 flex items-baseline gap-3">
          {/* Proportional figures, not tabular: at display size, equal-width
              digits make a number like this look loose. */}
          <span className="text-5xl font-semibold tracking-tight text-[var(--text-primary)]">
            ₦
            {Number(rate.amount).toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </span>
          {/* Only when there is something to compare against. An em dash beside
              a 48px number reads as a typo rather than as "no data". */}
          {hasChange ? (
            <ChangePill changePercent={rate.change_percent} size="lg" />
          ) : null}
        </p>
        <p className="mt-1 text-sm text-[var(--text-secondary)]">
          per 1 {rate.symbol.split('/')[0]}
        </p>
        {hasTrend ? (
          <div className="mt-3">
            <Sparkline
              points={rate.history}
              direction={direction}
              width={220}
              height={44}
              label={`${rate.symbol} over ${rate.history.length} published rates`}
            />
          </div>
        ) : null}
      </div>

      {/* The provenance, as a definition list rather than a sentence. An
          analyst reading a rate wants to know its source, its age and its
          cadence before they use it, and scanning four labelled rows is faster
          than parsing a paragraph. */}
      <dl className="grid min-w-0 max-w-full grid-cols-[auto_minmax(0,1fr)] gap-x-4 gap-y-1.5 text-xs">
        <dt className="text-[var(--text-tertiary)]">Published</dt>
        <dd className="text-[var(--text-secondary)]">
          {formatRelative(rate.observed_at)}
          {rate.stale ? (
            <span className="ml-1.5 text-[var(--color-stale)]">
              · stale, {Math.round(rate.age_hours)}h old
            </span>
          ) : null}
        </dd>

        <dt className="text-[var(--text-tertiary)]">Cadence</dt>
        <dd className="text-[var(--text-secondary)]">Once a day, not live</dd>

        <dt className="text-[var(--text-tertiary)]">Source</dt>
        <dd className="text-[var(--text-secondary)]">
          {rate.source_url ? (
            <a
              className="underline decoration-dotted underline-offset-2 hover:text-[var(--text-primary)]"
              href={rate.source_url}
              rel="nofollow noopener"
              target="_blank"
            >
              {sourceLabel(rate.source)}
            </a>
          ) : (
            sourceLabel(rate.source)
          )}
        </dd>

        <dt className="text-[var(--text-tertiary)]">Basis</dt>
        <dd className="max-w-[15rem] text-[var(--text-secondary)]">
          Published rate. Nigeria&rsquo;s parallel market trades separately and
          is not shown.
        </dd>

        {!hasChange ? (
          <>
            <dt className="text-[var(--text-tertiary)]">Change</dt>
            <dd className="max-w-[15rem] text-[var(--text-secondary)]">
              Not yet — only one publication recorded. A second gives a move.
            </dd>
          </>
        ) : null}
      </dl>
    </div>
  )
}

/** The remaining pairs, as a KPI row. */
export function RateTiles({ rates }: { rates: MarketRate[] }) {
  if (rates.length === 0) return null

  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
      {rates.map((rate) => {
        const direction = directionOf(rate.change_percent)
        return (
          <Panel key={rate.symbol} className="flex flex-col gap-1">
            <div className="flex items-center justify-between gap-2">
              <span className="field-label text-[var(--text-tertiary)]">
                {rate.symbol}
              </span>
              <ChangePill changePercent={rate.change_percent} />
            </div>
            <div className="flex items-end justify-between gap-2">
              <span className="text-xl font-semibold text-[var(--text-primary)]">
                ₦
                {Number(rate.amount).toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </span>
              <Sparkline points={rate.history} direction={direction} width={64} height={24} />
            </div>
            <span className="text-[0.6rem] text-[var(--text-tertiary)]">
              {rate.unit} · {formatRelative(rate.observed_at)}
            </span>
          </Panel>
        )
      })}
    </div>
  )
}

/* -- Index benchmarks ------------------------------------------------------ */

/**
 * Benchmarks from this platform's own listings, priced in naira **and** in
 * dollars at today's published rate.
 *
 * The dollar column is the point of this component and it exists nowhere else
 * in the product: it joins a figure we computed from Nigerian supplier listings
 * to a figure we fetched from the FX market, which is the question an importer
 * actually has. It is only rendered when a USD rate is on hand — a converted
 * number with no rate behind it would be exactly the kind of unsourced figure
 * this index refuses to publish.
 */
export function BenchmarkTable({
  benchmarks,
  usdRate,
}: {
  benchmarks: MarketRate[]
  usdRate: MarketRate | undefined
}) {
  if (benchmarks.length === 0) return null
  const usd = usdRate ? Number(usdRate.amount) : null

  return (
    <Panel padded={false} className="overflow-x-auto">
      <table className="w-full min-w-[34rem] text-sm">
        <caption className="sr-only">
          Median published price per category, from supplier listings held by
          this index.
        </caption>
        <thead>
          <tr className="border-b border-[var(--border-default)] text-left">
            <th scope="col" className="px-4 pb-2 pt-4 field-label font-semibold">
              Category
            </th>
            <th scope="col" className="px-4 pb-2 pt-4 field-label font-semibold text-right">
              Median price
            </th>
            {usd !== null ? (
              <th scope="col" className="px-4 pb-2 pt-4 field-label font-semibold text-right">
                At today&rsquo;s USD rate
              </th>
            ) : null}
            <th scope="col" className="px-4 pb-2 pt-4 field-label font-semibold text-right">
              Offers
            </th>
          </tr>
        </thead>
        <tbody className="divide-hairline">
          {benchmarks.map((benchmark) => {
            const naira = Number(benchmark.amount)
            return (
              <tr key={benchmark.symbol} className="hover:bg-[var(--surface-hover)]">
                <td className="px-4 py-2.5">
                  <span className="font-medium text-[var(--text-primary)]">
                    {benchmark.name}
                  </span>
                  <span className="ml-2 text-xs text-[var(--text-tertiary)]">
                    {benchmark.unit}
                  </span>
                </td>
                <td className="px-4 py-2.5 text-right tabular-nums text-[var(--text-primary)]">
                  ₦{naira.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                </td>
                {usd !== null ? (
                  <td className="px-4 py-2.5 text-right tabular-nums text-[var(--text-secondary)]">
                    $
                    {(naira / usd).toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </td>
                ) : null}
                <td className="px-4 py-2.5 text-right tabular-nums text-[var(--text-secondary)]">
                  {formatNumber(benchmark.observation_count)}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </Panel>
  )
}

export { CountryCode, REGIONS }
